// src/services/socket/SocketManager.js - WebSocket Connection Foundation
import { io } from 'socket.io-client';
import UniversalResponseHandler from './UniversalResponseHandler';

class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.universalHandler = null;
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    
    // Event listeners storage
    this.eventListeners = new Map();
    
    // Connection state callbacks
    this.onConnectionStateChange = null;
    this.onAuthStateChange = null;
    
    // Debug mode (can be toggled via env)
    this.debug = import.meta.env.VITE_DEBUG_SOCKET === 'true';
    
    // Activity tracking
    this.lastActivityTime = Date.now();
    this.keepAliveInterval = null;
    this.activityListeners = [];
    
    // Start activity tracking when constructed
    this.setupActivityTracking();
    
    this.log('🔌 SocketManager initialized');
  }

  // ================================
  // ACTIVITY TRACKING
  // ================================

  /**
   * Set up activity detection for meaningful user interactions
   */
  setupActivityTracking() {
    // Events that indicate real user activity
    const activityEvents = [
      'click', 
      'keydown', 
      'touchstart',
      'scroll'  // throttled
    ];

    // Add activity listeners
    activityEvents.forEach(eventType => {
      const listener = this.throttle(() => {
        this.recordActivity();
      }, eventType === 'scroll' ? 1000 : 500); // Throttle scroll more aggressively

      document.addEventListener(eventType, listener, { passive: true });
      this.activityListeners.push({ event: eventType, listener });
    });

    this.log('📊 Activity tracking initialized');
  }

  /**
   * Record user activity and update timestamp
   */
  recordActivity() {
    this.lastActivityTime = Date.now();
    this.log('👆 User activity detected');
  }

  /**
   * Start sending keep-alive signals when connected
   */
  startKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }

    this.keepAliveInterval = setInterval(() => {
      this.sendKeepAliveIfActive();
    }, 180000); // Every 3 minutes

    this.log('💓 Keep-alive heartbeat started (3 minute interval)');
  }

  /**
   * Send keep-alive only if user was active in last 5 minutes
   */
  sendKeepAliveIfActive() {
    const timeSinceActivity = Date.now() - this.lastActivityTime;
    const fiveMinutes = 5 * 60 * 1000;

    if (timeSinceActivity < fiveMinutes && this.isAuthenticated) {
      this.emit('keep_alive', {
        last_activity: this.lastActivityTime,
        timestamp: Date.now()
      });
      this.log('💓 Keep-alive sent (user active)');
    } else {
      this.log('😴 Keep-alive skipped (user idle)');
    }
  }

  /**
   * Stop keep-alive interval
   */
  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
      this.log('💓 Keep-alive stopped');
    }
  }

  /**
   * Cleanup activity listeners
   */
  cleanupActivityTracking() {
    this.activityListeners.forEach(({ event, listener }) => {
      document.removeEventListener(event, listener);
    });
    this.activityListeners = [];
    this.stopKeepAlive();
    this.log('📊 Activity tracking cleaned up');
  }

  /**
   * Simple throttle utility
   */
  throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;
    return function (...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }

  // ================================
  // CONNECTION MANAGEMENT
  // ================================

  /**
   * Connect to WebSocket server with JWT authentication
   * @param {string} token - JWT access token
   * @returns {Promise<boolean>} - Success/failure
   */
  async connect(token) {
    if (!token) {
      this.log('❌ No token provided for WebSocket connection');
      return false;
    }

    if (this.isConnecting) {
      this.log('⏳ Connection already in progress');
      return false;
    }

    if (this.isConnected && this.isAuthenticated) {
      this.log('✅ Already connected and authenticated');
      return true;
    }

    try {
      this.isConnecting = true;
      this.updateConnectionState('connecting');
      
      this.log('🔌 Connecting to WebSocket server...');
      
      // Create socket connection with auth
      const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5001';
      
      this.socket = io(socketUrl, {
        auth: {
          token: token
        },
        transports: ['websocket', 'polling'], // Fallback to polling if needed
        timeout: 10000, // 10 second connection timeout
        reconnection: false // We'll handle reconnection manually
      });

      this.universalHandler = new UniversalResponseHandler(this.socket);

      // Set up connection event handlers
      this.setupConnectionEvents();

      // Wait for connection result
      const connected = await this.waitForConnection();
      
      if (connected) {
        this.isConnected = true;
        this.isAuthenticated = true;
        this.reconnectAttempts = 0;
        this.updateConnectionState('connected');
        this.updateAuthState(true);
        
        // START KEEP-ALIVE WHEN CONNECTED
        this.startKeepAlive();
        
        this.log('✅ WebSocket connected and authenticated');
        return true;
      } else {
        this.log('❌ WebSocket connection failed');
        this.cleanup();
        return false;
      }

    } catch (error) {
      this.log('💥 Connection error:', error);
      this.cleanup();
      return false;
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.log('👋 Disconnecting from WebSocket server');
    
    // Stop keep-alive first
    this.stopKeepAlive();
    
    // **CRITICAL FIX**: Set flags FIRST to prevent any new operations
    this.isConnected = false;
    this.isAuthenticated = false;
    this.isConnecting = false;
    
    // Update states immediately
    this.updateAuthState(false);
    this.updateConnectionState('disconnected');
    
    // Clean up the socket connection with proper sequencing
    if (this.socket) {
      // Remove all listeners BEFORE disconnecting
      this.socket.removeAllListeners();
      
      // Disconnect with force flag to prevent reconnection attempts
      this.socket.disconnect(true);
      
      // Nullify the socket reference
      this.socket = null;
    }
    
    // Clean up universal handler
    if (this.universalHandler) {
      this.universalHandler.cleanup();
      this.universalHandler = null;
    }
    
    // Clear event listeners tracking
    this.eventListeners.clear();
    
    this.log('✅ WebSocket disconnected cleanly');
  }

  /**
   * Reconnect with exponential backoff
   */
  async reconnect(token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log('❌ Max reconnection attempts reached');
      this.updateConnectionState('failed');
      return false;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    this.log(`🔄 Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    this.updateConnectionState('reconnecting');
    
    await this.sleep(delay);
    
    return this.connect(token);
  }

  // ================================
  // EVENT COMMUNICATION
  // ================================

  /**
   * Emit an event to the server
   * @param {string} event - Event name
   * @param {object} data - Event data
   * @returns {boolean} - Success/failure
   */
  emit(event, data = {}) {
    if (!this.isAuthenticated) {
      this.log('❌ Cannot emit event - not authenticated:', event);
      return false;
    }

    this.log('📤 Emitting event:', event, data);
    this.socket.emit(event, data);
    return true;
  }

  /**
   * Listen for an event from the server
   * @param {string} event - Event name
   * @param {function} callback - Event handler
   */
  on(event, callback) {
    if (!this.universalHandler) {
      this.log('❌ Cannot listen for event - no universal handler:', event);
      return;
    }

    this.log('👂 Listening for universal response event:', event);
    this.universalHandler.on(event, callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {function} callback - Event handler to remove
   */
  off(event, callback) {
    if (!this.universalHandler) return;
    this.universalHandler.off(event, callback);
    this.log('🔇 Removed universal response listener for event:', event);
  }

  // ================================
  // CONNECTION STATE MANAGEMENT
  // ================================

  /**
   * Set up socket connection event handlers
   */
  setupConnectionEvents() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.log('🎉 Socket connected');
    });

    this.socket.on('disconnect', (reason) => {
      this.log('💔 Socket disconnected:', reason);
      this.isConnected = false;
      this.isAuthenticated = false;
      this.updateConnectionState('disconnected');
      this.updateAuthState(false);
      
      // Don't auto-reconnect on manual disconnect
      if (reason !== 'io client disconnect') {
        this.log('🔄 Will attempt reconnection...');
      }
    });

    this.socket.on('connect_error', (error) => {
      this.log('❌ Connection error:', error);
    });

    // Handle server errors
    this.socket.on('error', (error) => {
      this.log('💥 Socket error:', error);
    });
  }

  /**
   * Wait for connection to be established
   * @returns {Promise<boolean>}
   */
  waitForConnection() {
    return new Promise((resolve) => {
      if (!this.socket || !this.universalHandler) {
        resolve(false);
        return;
      }

      const timeout = setTimeout(() => {
        resolve(false);
      }, 10000);

      // Use universalHandler to listen for connection_established
      this.universalHandler.on('connection_established', (data) => {
        clearTimeout(timeout);
        this.log('✅ Authentication successful via universal response:', data);
        resolve(true);
      });

      this.socket.once('connect_error', () => {
        clearTimeout(timeout);
        resolve(false);
      });

      this.socket.once('authentication_failed', () => {
        clearTimeout(timeout);
        resolve(false);
      });
    });
  }

  /**
   * Clean up socket connection and event listeners
   */
  cleanup() {
    this.isConnected = false;
    this.isConnecting = false;
    
    // Clean up activity tracking
    this.cleanupActivityTracking();
    
    if (this.universalHandler) {
      this.universalHandler.cleanup();
      this.universalHandler = null;
    }
    
    if (this.socket) {
      // Remove all custom event listeners
      this.eventListeners.forEach((callbacks, event) => {
        callbacks.forEach(callback => {
          this.socket.off(event, callback);
        });
      });
      this.eventListeners.clear();
      
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.updateConnectionState('disconnected');
  }

  // ================================
  // STATE CALLBACKS
  // ================================

  /**
   * Set callback for connection state changes
   * @param {function} callback - (state) => void
   */
  setConnectionStateCallback(callback) {
    this.onConnectionStateChange = callback;
  }

  /**
   * Set callback for auth state changes
   * @param {function} callback - (isAuthenticated) => void
   */
  setAuthStateCallback(callback) {
    this.onAuthStateChange = callback;
  }

  updateConnectionState(state) {
    this.log('🔄 Connection state:', state);
    if (this.onConnectionStateChange) {
      this.onConnectionStateChange(state);
    }
  }

  updateAuthState(isAuthenticated) {
    this.log('🔐 Auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
    if (this.onAuthStateChange) {
      this.onAuthStateChange(isAuthenticated);
    }
  }

  // ================================
  // UTILITY METHODS
  // ================================

  /**
   * Get current connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isAuthenticated: this.isAuthenticated,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * Sleep utility for reconnection delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Debug logging
   */
  log(message, ...args) {
    if (this.debug) {
      console.log(`[SocketManager] ${message}`, ...args);
    }
  }
}

// Create singleton instance
const socketManager = new SocketManager();

export default socketManager;